import { useCallback } from 'react';
import { useMutation, useInfiniteQuery, useQueryClient, useQuery } from 'react-query';
import { useAxios } from '../context/AxiosContext';
import { toast } from 'react-toastify';
import { GetRolesResponse, RoleToSend } from '../@types/Roles';

type SortMode = 'ASC' | 'DESC';

export default function useRoles({
  limit = undefined,
  sorting = 'ASC',
  name = undefined
}: {
  limit?: number | undefined;
  sorting?: SortMode;
  name?: string | undefined;
} = {}) {
  const queryCache = useQueryClient();
  const { authAxios } = useAxios();

  const fetchFormData = useCallback(
    async ({ limit, sorting, name }) => {
      const response = await authAxios.get<GetRolesResponse>('/roles', {
        params: {
          first: limit,
          mode: sorting,
          name,
          inactive: true
        }
      });
      return response.data?.rolesByName;
    },
    [authAxios]
  );

  const deactivateRolesMutation = useMutation((data: RoleToSend[]) => authAxios.put('/roles', { roles: data }), {
    onSuccess: (_data, variables: RoleToSend[]) => {
      queryCache.invalidateQueries(['roles']);
      const result = Array.from(new Set(variables.map((item: RoleToSend) => item.active)));
      if (result.length) {
        toast.success(
          `Successfully ${
            result.length > 1 ? 'changed status of roles' : `${result[0] ? 'activated' : 'deactivated'} role(s)`
          } `,
          { position: 'top-center' }
        );
      }
    }
  });

  const createRolesMutation = useMutation((data) => authAxios.post('/roles', { roles: data }), {
    onSuccess: (data, variables) => {
      queryCache.invalidateQueries(['roles']);
      toast.success('The role has been saved successfully', { position: 'top-center' });
    },
    onError: () => {
      toast.error('Failed to save the role.', { position: 'top-center' });
    }
  });

  const reactQuery = useInfiniteQuery(
    ['roles', limit, sorting, name],
    ({ pageParam = undefined }) => fetchFormData({ limit, pageParam, sorting, name }),
    {
      suspense: true,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      getNextPageParam: (data, pages) => data?.pageInfo.hasNextPage && data?.pageInfo.endCursor,
      getPreviousPageParam: (data, pages) => data?.pageInfo.hasPreviousPage && data?.pageInfo.startCursor
    }
  );

  return {
    ...reactQuery,
    deactivateRoles: deactivateRolesMutation.mutateAsync,
    createRoles: createRolesMutation.mutateAsync
  };
}

export const useRole = ({ id }: { id: string | null }) => {
  const { authAxios } = useAxios();

  const fetchRole = useCallback(
    async (id): Promise<any> => {
      const response = await authAxios.get<any>(`/roles/${id}`);
      return response.data;
    },
    [authAxios]
  );

  return useQuery(`fetch-role-${id}`, () => fetchRole(id), {
    refetchOnWindowFocus: false,
    enabled: id !== null,
    suspense: true
  });
};
